// ADR-0163 (#0770) — convertir una consulta del propietario en observación,
// dentro del mismo drawer del hilo (vista «convertir»; no anida un drawer).
//
// Decisión 9 del founder: la app sugiere y el jefe corrige. Al entrar a esta
// vista con `canSuggest`, el toque en «Convertir en observación» ES el pedido
// a la IA (el rótulo lo dice); sin `canSuggest` no se llama nada y el jefe
// clasifica a mano con el mismo formulario. Si la IA falla, igual.
//
// Decisión 10: al contratista le llega SÓLO lo que escribe el jefe. La
// descripción nace vacía, con lo que escribió el propietario a la vista para
// copiar lo necesario.
//
// El estado del formulario se arma UNA vez al entrar (useState con
// inicializador), nunca con un efecto atado al detalle: el hilo se revisa
// cada 15 s y no puede borrar lo que el jefe va escribiendo.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  CONVERSION_DESCRIPCION_MAX,
  CONVERSION_NOTA_MAX,
  CRITICIDAD_LABEL,
  OTR_ESPECIALIDAD,
  OWNER_INQUIRY_CONVERSION_MAX_PHOTOS,
  SEVERIDAD_PREGUNTA,
  SUGERIDO_POR_LA_IA,
  type OwnerInquiryConversionRequest,
  type OwnerInquiryConversionResponse,
  type OwnerInquiryConversionSugerencia,
  type OwnerInquiryDetail,
} from "@bitacorapro/api-types";
import {
  deriveObservationSeverityPartial,
  findTaxonomyPartida,
  p1ForcedByRule,
  recintosPrivados,
  taxonomyObservationTitle,
} from "@bitacorapro/core-engine";
import { taxonomyRegistry, TAXONOMY_VERSION_CURRENT } from "@bitacorapro/postventa-taxonomy";
import {
  ClasificacionFields,
  partidaDeEspecialidad,
  type ClasificacionCampo,
  type ClasificacionValue,
} from "./ClasificacionFields";
import styles from "./ConvertirConsultaForm.module.css";
import { conversionEjesMissing, conversionErrorText } from "../lib/api/conversionErrors";
import { mensajeDeError } from "../lib/api/error-message";
import {
  useConvertOwnerInquiry,
  useOwnerInquiryAttachmentUrls,
  useSuggestOwnerInquiryConversion,
} from "../lib/api/queries";
import { contadorCaracteres } from "../lib/postventa/consultasList";

export const SUGERENCIA_RAZON =
  "Sugerimos la clasificación con IA a partir del texto y las fotos marcadas. Revísala y corrige lo que haga falta.";
export const SUGERENCIA_VACIA = "La IA no pudo clasificar: elige a mano.";
export const SUGERENCIA_ERROR = "No pudimos sugerir. Clasifica a mano.";
export const DESCRIPCION_AYUDA =
  "Es lo único que verá el contratista. Puedes copiar de la consulta lo necesario; no incluyas datos personales del propietario.";
export const CONVERT_ERROR_FALLBACK = "No pudimos crear la observación. Intenta de nuevo.";

type Respuesta = boolean | null;

type Foto = { id: string; createdAt: string };

/** Las fotos del propietario de la consulta, de la más nueva a la más vieja. */
export function fotosDelPropietario(detail: OwnerInquiryDetail): Foto[] {
  return detail.entries
    .filter((e) => e.kind === "consulta" || e.kind === "seguimiento")
    .flatMap((e) =>
      e.attachments
        .filter((a) => a.tipo === "foto")
        .map((a) => ({ id: a.id, createdAt: e.createdAt }))
    )
    .reverse();
}

export function ConvertirConsultaForm({
  inquiryId,
  detail,
  canSuggest,
  onVolver,
  onConvertida,
  onOcupado,
}: {
  inquiryId: string;
  detail: OwnerInquiryDetail;
  canSuggest: boolean;
  onVolver: () => void;
  onConvertida: (res: OwnerInquiryConversionResponse) => void;
  /** En vuelo: el drawer no se cierra con Escape ni la X. */
  onOcupado: (ocupado: boolean) => void;
}) {
  const catalog = taxonomyRegistry.get(TAXONOMY_VERSION_CURRENT)!;
  const especialidades = useMemo(
    () => catalog.especialidades.filter((e) => e.codigo !== OTR_ESPECIALIDAD),
    [catalog]
  );
  const recintos = useMemo(() => recintosPrivados(catalog), [catalog]);

  // Todo se arma al entrar; el sondeo del hilo no lo toca.
  const [fotos] = useState<Foto[]>(() => fotosDelPropietario(detail));
  const [palabras] = useState(() =>
    detail.entries.filter((e) => e.kind === "consulta" || e.kind === "seguimiento")
  );
  const [marcadas, setMarcadas] = useState<string[]>(() =>
    fotos.slice(0, OWNER_INQUIRY_CONVERSION_MAX_PHOTOS).map((f) => f.id)
  );
  const [cls, setCls] = useState<ClasificacionValue>({
    especialidad: "",
    partida: "",
    recinto: "",
    elemento: "",
    tipoFalla: "",
  });
  const [aguaActiva, setAguaActiva] = useState<Respuesta>(null);
  const [anchoMm, setAnchoMm] = useState("");
  const [p1, setP1] = useState<Respuesta>(null);
  const [p2, setP2] = useState<Respuesta>(null);
  const [p3, setP3] = useState<Respuesta>(null);
  const [descripcion, setDescripcion] = useState("");
  const [nota, setNota] = useState("");
  const [sugeridos, setSugeridos] = useState<Partial<Record<string, boolean>>>({});
  const [propuesta, setPropuesta] = useState<string | null>(null);
  const [sugeridaCon, setSugeridaCon] = useState<string | null>(null);

  const suggest = useSuggestOwnerInquiryConversion();
  const convert = useConvertOwnerInquiry();
  const attemptRef = useRef<{ firma: string; key: string } | null>(null);
  const ids = {
    form: useId(),
    agua: useId(),
    ancho: useId(),
    desc: useId(),
    descHint: useId(),
    nota: useId(),
    fotos: useId(),
  };

  const ocupado = convert.isPending;
  useEffect(() => {
    onOcupado(ocupado);
  }, [ocupado, onOcupado]);
  // Al desmontar (éxito o «Volver») el drawer deja de estar ocupado: el
  // `onSuccess` de la conversión saca esta vista antes de que el formulario
  // alcance a pintarse con `isPending=false`, y sin esto el drawer quedaba
  // sin poder cerrarse.
  useEffect(() => () => onOcupado(false), [onOcupado]);
  // Mientras la IA piensa, la clasificación y la criticidad no se tocan: la
  // respuesta reemplaza esos campos y pisaría en silencio lo que el jefe
  // eligió (decisión 9: el jefe corrige DESPUÉS de ver la sugerencia).
  const sugiriendo = suggest.isPending;
  const bloqueado = ocupado || sugiriendo;

  // La vista nace con el foco en ella: el botón que la abrió desaparece.
  const raizRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    raizRef.current?.focus();
  }, []);

  function aplicar(s: OwnerInquiryConversionSugerencia) {
    const partida = s.codPartida ? findTaxonomyPartida(catalog, s.codPartida) : undefined;
    const next: ClasificacionValue = {
      especialidad: partida?.especialidad.codigo ?? "",
      partida: partida?.partida.codigo ?? "",
      recinto: s.codRecinto ?? "",
      elemento: s.codElemento ?? "",
      tipoFalla: s.codTipoFalla ?? "",
    };
    setCls(next);
    setAguaActiva(s.aguaActiva);
    setAnchoMm(s.anchoMm === null ? "" : String(s.anchoMm));
    setP1(s.p1Risk);
    setP2(s.p2FunctionOrProgression);
    setP3(s.p3MinorFunctional);
    setSugeridos({
      especialidad: next.especialidad !== "",
      partida: next.partida !== "",
      recinto: next.recinto !== "",
      elemento: next.elemento !== "",
      tipoFalla: next.tipoFalla !== "",
      p1: s.p1Risk !== null,
      p2: s.p2FunctionOrProgression !== null,
      p3: s.p3MinorFunctional !== null,
    });
  }

  function pedirSugerencia(seleccion: string[]) {
    if (!canSuggest) return;
    setSugeridaCon(JSON.stringify(seleccion));
    suggest.mutate(
      { id: inquiryId, body: { attachmentIds: seleccion } },
      {
        onSuccess: (res) => {
          setPropuesta(res.propuesta);
          if (res.sugerencia) aplicar(res.sugerencia);
          // Sin clasificación para estas fotos: los valores quedan (son del
          // jefe desde ahora) pero sin el tag, que diría lo contrario del
          // aviso «elige a mano»; sin sobre, la conversión no cuenta como
          // sugerida.
          else setSugeridos({});
        },
      }
    );
  }

  // El pedido de la sugerencia es el toque en «Convertir en observación»:
  // se pide al entrar. Sin guard de ref a propósito: en StrictMode el
  // desmontaje simulado separa el observer de la primera mutación y, si no
  // se vuelve a pedir, `isPending` queda en true para siempre (la vista se
  // quedaba en «Sugiriendo…»). En dev salen dos pedidos y gana el último;
  // en producción el efecto corre una vez.
  useEffect(() => {
    pedirSugerencia(marcadas);
    // Una sola vez al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tipo = catalog.tiposFalla.find((t) => t.codigo === cls.tipoFalla);
  const forzado = p1ForcedByRule(cls.especialidad || null, cls.tipoFalla || null) !== null;
  const p1Efectivo: Respuesta = forzado ? true : p1;
  const severidad = deriveObservationSeverityPartial(p1Efectivo, p2, p3);
  const partidaOk = partidaDeEspecialidad(catalog, cls.especialidad, cls.partida);
  const titulo = taxonomyObservationTitle(catalog, {
    codTipoFalla: cls.tipoFalla || null,
    codElemento: cls.elemento || null,
    codRecinto: cls.recinto || null,
  });
  const descripcionOk = descripcion.trim().length > 0;
  const aguaOk = tipo?.campoAdicional !== "agua_activa" || aguaActiva !== null;
  const listo =
    !bloqueado &&
    partidaOk &&
    cls.recinto !== "" &&
    cls.elemento !== "" &&
    cls.tipoFalla !== "" &&
    aguaOk &&
    severidad !== null &&
    descripcionOk;
  const cambioDeFotos = sugeridaCon !== null && sugeridaCon !== JSON.stringify(marcadas);

  function cambiarCls(next: ClasificacionValue, campo: ClasificacionCampo) {
    setCls(next);
    setSugeridos((s) => ({
      ...s,
      [campo]: false,
      ...(campo === "especialidad" ? { partida: false } : {}),
    }));
    if (campo === "tipoFalla") {
      setAguaActiva(null);
      setAnchoMm("");
    }
  }

  function marcar(id: string, on: boolean) {
    setMarcadas((prev) =>
      on
        ? prev.length >= OWNER_INQUIRY_CONVERSION_MAX_PHOTOS || prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((x) => x !== id)
    );
  }

  function enviar() {
    if (!listo) return;
    const ancho = tipo?.campoAdicional === "ancho_mm" && anchoMm.trim() ? Number(anchoMm) : null;
    const body: OwnerInquiryConversionRequest = {
      codRecinto: cls.recinto,
      codElemento: cls.elemento,
      codPartida: cls.partida,
      codTipoFalla: cls.tipoFalla,
      aguaActiva: tipo?.campoAdicional === "agua_activa" ? aguaActiva : null,
      anchoMm: ancho !== null && Number.isFinite(ancho) && ancho > 0 ? ancho : null,
      p1Risk: p1Efectivo,
      p2FunctionOrProgression: p1Efectivo ? null : p2,
      p3MinorFunctional: p1Efectivo || p2 ? null : p3,
      descripcion: descripcion.trim(),
      ...(nota.trim() ? { nota: nota.trim() } : {}),
      attachmentIds: marcadas,
      ...(propuesta ? { propuesta } : {}),
    };
    // La clave va atada al contenido: un reintento del mismo envío la
    // reutiliza (el servidor devuelve la misma conversión).
    const firma = JSON.stringify(body);
    const attempt =
      attemptRef.current?.firma === firma
        ? attemptRef.current
        : { firma, key: crypto.randomUUID() };
    attemptRef.current = attempt;
    convert.mutate(
      { id: inquiryId, body, idempotencyKey: attempt.key, assetId: detail.inquiry.assetId },
      { onSuccess: (res) => onConvertida(res) }
    );
  }

  const ejesFaltan = conversionEjesMissing(convert.error);
  const errorPropio = conversionErrorText(convert.error);

  const pregunta = (
    key: "p1" | "p2" | "p3",
    texto: string,
    valor: Respuesta,
    set: (v: Respuesta) => void,
    bloqueada = false
  ) => (
    <fieldset className={styles.pregunta} disabled={bloqueado || bloqueada}>
      <legend className={styles.label}>
        {texto}
        {sugeridos[key] && <span className="st-pill st-pill--info">{SUGERIDO_POR_LA_IA}</span>}
      </legend>
      {bloqueada && (
        <p className={styles.hint}>
          Por el tipo de observación y la especialidad, la respuesta es sí.
        </p>
      )}
      <div className={styles.opciones}>
        {([true, false] as const).map((v) => (
          <label key={String(v)} className={styles.opcion}>
            <input
              type="radio"
              name={`${ids.form}-${key}`}
              checked={valor === v}
              onChange={() => {
                set(v);
                setSugeridos((s) => ({ ...s, [key]: false }));
              }}
            />
            {v ? "Sí" : "No"}
          </label>
        ))}
      </div>
    </fieldset>
  );

  return (
    <div
      className={styles.form}
      ref={raizRef}
      tabIndex={-1}
      role="region"
      aria-label="Convertir en observación"
    >
      <section aria-labelledby={`${ids.form}-palabras`}>
        <div className="drawer__section-label" id={`${ids.form}-palabras`}>
          Lo que escribió el propietario
        </div>
        <ol className={styles.palabras}>
          {palabras.map((e) =>
            e.body ? (
              <li key={e.id} className="ev__text">
                {e.body}
              </li>
            ) : null
          )}
        </ol>
      </section>

      {fotos.length > 0 && (
        <FotosConsulta
          inquiryId={inquiryId}
          fotos={fotos}
          marcadas={marcadas}
          onMarcar={marcar}
          disabled={ocupado}
          labelId={ids.fotos}
        />
      )}

      <section aria-labelledby={`${ids.form}-cls`} className={styles.bloque}>
        <div className="drawer__section-label" id={`${ids.form}-cls`}>
          Clasificación
        </div>
        {canSuggest && (
          <p className={styles.hint} role="status" aria-live="polite">
            {suggest.isPending
              ? "Sugiriendo con IA…"
              : suggest.isError
                ? SUGERENCIA_ERROR
                : suggest.isSuccess && !suggest.data.sugerencia
                  ? SUGERENCIA_VACIA
                  : SUGERENCIA_RAZON}
          </p>
        )}
        {canSuggest && (suggest.isError || cambioDeFotos) && !suggest.isPending && (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={ocupado}
            onClick={() => pedirSugerencia(marcadas)}
          >
            {suggest.isError ? "Reintentar" : "Volver a sugerir"}
          </button>
        )}
        <div aria-busy={suggest.isPending || undefined} className={styles.campos}>
          <ClasificacionFields
            catalog={catalog}
            idPrefix={`${ids.form}-cls`}
            value={cls}
            onChange={cambiarCls}
            especialidades={especialidades}
            recintos={recintos}
            partidaLabel="Tipo de reparación"
            sugeridos={sugeridos}
            disabled={bloqueado}
          />
          {tipo?.campoAdicional === "agua_activa" && (
            <fieldset className={styles.pregunta} disabled={bloqueado}>
              <legend className={styles.label} id={ids.agua}>
                ¿Hay agua activa (sigue saliendo o goteando)?
              </legend>
              <div className={styles.opciones}>
                {([true, false] as const).map((v) => (
                  <label key={String(v)} className={styles.opcion}>
                    <input
                      type="radio"
                      name={`${ids.form}-agua`}
                      checked={aguaActiva === v}
                      onChange={() => setAguaActiva(v)}
                    />
                    {v ? "Sí" : "No"}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          {tipo?.campoAdicional === "ancho_mm" && (
            <div className={styles.campo}>
              <label className={styles.label} htmlFor={ids.ancho}>
                Ancho de la fisura en milímetros (opcional)
              </label>
              <input
                id={ids.ancho}
                className="input"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.1"
                value={anchoMm}
                disabled={bloqueado}
                onChange={(e) => setAnchoMm(e.target.value)}
              />
            </div>
          )}
        </div>
      </section>

      <section aria-labelledby={`${ids.form}-sev`} className={styles.bloque}>
        <div className="drawer__section-label" id={`${ids.form}-sev`}>
          Criticidad
        </div>
        {pregunta("p1", SEVERIDAD_PREGUNTA.p1Risk, p1Efectivo, setP1, forzado)}
        {p1Efectivo === false &&
          pregunta("p2", SEVERIDAD_PREGUNTA.p2FunctionOrProgression, p2, setP2)}
        {p1Efectivo === false &&
          p2 === false &&
          pregunta("p3", SEVERIDAD_PREGUNTA.p3MinorFunctional, p3, setP3)}
        <p className={styles.hint}>
          Criticidad resultante:{" "}
          <strong>{severidad ? CRITICIDAD_LABEL[severidad] : "falta responder"}</strong>
        </p>
      </section>

      <section className={styles.bloque}>
        <div className={styles.campo}>
          <label className={styles.label} htmlFor={ids.desc}>
            Descripción para el contratista
          </label>
          <textarea
            id={ids.desc}
            className="textarea"
            rows={4}
            required
            aria-required="true"
            maxLength={CONVERSION_DESCRIPCION_MAX}
            value={descripcion}
            readOnly={ocupado}
            aria-describedby={ids.descHint}
            onChange={(e) => setDescripcion(e.target.value)}
          />
          <span className="field__hint" id={ids.descHint}>
            {contadorCaracteres(descripcion.length, CONVERSION_DESCRIPCION_MAX)} ·{" "}
            {DESCRIPCION_AYUDA}
          </span>
        </div>
        <div className={styles.campo}>
          <label className={styles.label} htmlFor={ids.nota}>
            Nota para el propietario (opcional)
          </label>
          <textarea
            id={ids.nota}
            className="textarea"
            rows={2}
            maxLength={CONVERSION_NOTA_MAX}
            value={nota}
            readOnly={ocupado}
            onChange={(e) => setNota(e.target.value)}
          />
          <span className="field__hint">
            {contadorCaracteres(nota.length, CONVERSION_NOTA_MAX)} · La verá en la consulta, debajo
            de «Registramos tu consulta como una observación…».
          </span>
        </div>
      </section>

      {convert.isError && (
        <div className="auth-error" role="alert">
          {ejesFaltan ? (
            <>
              <p>Falta completar:</p>
              <ul>
                {ejesFaltan.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </>
          ) : (
            <p>{errorPropio ?? mensajeDeError(convert.error, CONVERT_ERROR_FALLBACK)}</p>
          )}
        </div>
      )}

      <div className={styles.confirmacion}>
        <p className={styles.hint}>
          Se creará la observación «{titulo}» en {detail.inquiry.propiedadLabel} con{" "}
          {marcadas.length === 1 ? "1 foto" : `${marcadas.length} fotos`}. El propietario la verá
          como «desde tu consulta» y la consulta quedará cerrada. No se puede deshacer.
        </p>
        <div className="consulta-reply__actions">
          <button
            type="button"
            className="btn btn--secondary"
            disabled={ocupado}
            onClick={onVolver}
          >
            Volver
          </button>
          <button type="button" className="btn btn--primary" disabled={!listo} onClick={enviar}>
            {ocupado ? "Creando…" : "Crear observación"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Las fotos del propietario con casilla: las 10 más recientes nacen
 *  marcadas y el tope es 10 (el de la IA). Firmadas con la ruta del personal. */
function FotosConsulta({
  inquiryId,
  fotos,
  marcadas,
  onMarcar,
  disabled,
  labelId,
}: {
  inquiryId: string;
  fotos: readonly Foto[];
  marcadas: readonly string[];
  onMarcar: (id: string, on: boolean) => void;
  disabled: boolean;
  labelId: string;
}) {
  const urls = useOwnerInquiryAttachmentUrls(
    inquiryId,
    fotos.map((f) => f.id)
  );
  const lleno = marcadas.length >= OWNER_INQUIRY_CONVERSION_MAX_PHOTOS;
  return (
    <section aria-labelledby={labelId} className={styles.bloque}>
      <div className="drawer__section-label" id={labelId}>
        Fotos del propietario ({marcadas.length} de {OWNER_INQUIRY_CONVERSION_MAX_PHOTOS} marcadas)
      </div>
      <p className={styles.hint}>
        Las marcadas quedan en la observación. Sólo las ven el equipo y el propietario, nunca el
        contratista.
      </p>
      <ul className={styles.fotos}>
        {fotos.map((f, i) => {
          const url = urls.data?.get(f.id);
          const on = marcadas.includes(f.id);
          return (
            <li key={f.id}>
              <label className={styles.foto}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={disabled || (!on && lleno)}
                  onChange={(e) => onMarcar(f.id, e.target.checked)}
                />
                <span className="evc__thumb">{url && <img src={url} alt="" />}</span>
                <span className="sr-only">Foto {i + 1}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {urls.isError && (
        <p className="auth-error" role="alert">
          No pudimos cargar las fotos.
        </p>
      )}
    </section>
  );
}
